/**
 * CartalVerifiedPartnersStrip — Phase 2F → iter290
 * ────────────────────────────────────────────────────────────────
 * Drop-in strip that surfaces VERIFIED CARTAL partners for a given
 * business category on any service page (Insurance / Financing /
 * Banks / etc.).
 *
 * Contract:
 *  - When the platform has ≥1 verified, non-demo partner in the
 *    supplied category, render a compact strip linking each partner
 *    to its public profile.
 *  - When no partners exist (or the city filter yields none), render
 *    a minimal empty note under the same strip so the section never
 *    becomes a dead-end.
 *  - Auto-scoped: uses the already-hardened /platform/public/companies
 *    endpoint which itself only returns verified & non-demo businesses.
 *
 * iter290:
 *  - City filter (free-text input + quick-chip presets) queries the
 *    backend `city` param directly. Owner asked to REMOVE the
 *    "عرض الكل" link — replaced with the city filter row.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, MapPin, X } from "lucide-react";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";

const QUICK_CITIES_AR = ["الرياض", "جدة", "الدمام", "مكة", "المدينة", "الخبر"];
const QUICK_CITIES_EN = ["Riyadh", "Jeddah", "Dammam", "Makkah", "Madinah", "Khobar"];

export default function CartalVerifiedPartnersStrip({ categories, ctaAr, ctaEn }) {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [city, setCity] = useState("");
  const [cityInput, setCityInput] = useState("");
  const cats = Array.isArray(categories) ? categories : [categories];
  const catsKey = cats.join(",");

  useEffect(() => {
    let cancelled = false;
    Promise.all(cats.map((c) => {
      const params = { category: c, limit: 8 };
      if (city.trim()) params.city = city.trim();
      return apiClient.get("/platform/public/companies", { params })
        .then(({ data }) => data.items || [])
        .catch(() => []);
    })).then((lists) => {
      if (cancelled) return;
      const merged = lists.flat();
      const seen = new Set();
      const clean = merged.filter((b) => {
        if (seen.has(b.id)) return false;
        seen.add(b.id);
        return true;
      }).slice(0, 8);
      setItems(clean);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catsKey, city]);

  const quickCities = lang === "ar" ? QUICK_CITIES_AR : QUICK_CITIES_EN;
  const isAr = lang === "ar";

  const applyCity = (val) => {
    setCity(val);
    setCityInput(val);
  };

  const clearCity = () => {
    setCity("");
    setCityInput("");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    applyCity(cityInput.trim());
  };

  const showEmpty = items.length === 0 && (city.trim().length > 0);

  // If nothing to show at all and no filter has been applied, remain a
  // silent no-op (preserves the pre-iter290 behaviour on pages that
  // simply have no verified partners in the category yet).
  if (items.length === 0 && !city.trim()) return null;

  return (
    <section className="mt-10 mb-8" data-testid="cartal-verified-partners">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="overline text-[#8a6f2e] font-bold tracking-widest text-[11px]">
            {isAr ? "شركاء موثّقون داخل كرتال" : "Verified CARTAL partners"}
          </div>
          <h2 className="font-display text-xl text-[#0a0a0a] mt-1">
            {isAr ? (ctaAr || "اختر مقدم الخدمة المناسب لك") : (ctaEn || "Choose your provider")}
          </h2>
        </div>
      </div>

      {/* City filter row */}
      <form
        onSubmit={onSubmit}
        className="mb-4 flex flex-wrap items-center gap-2"
        data-testid="partners-city-filter"
      >
        <div
          className="inline-flex items-center gap-1.5 rounded-full border border-[#0a0a0a]/10 bg-white px-3 py-1.5 text-xs shrink-0"
          dir={isAr ? "rtl" : "ltr"}
        >
          <MapPin className="w-3.5 h-3.5 text-[#8a6f2e]" />
          <input
            type="text"
            value={cityInput}
            onChange={(e) => setCityInput(e.target.value)}
            placeholder={isAr ? "ابحث بالمدينة (مثال: جدة)" : "Filter by city (e.g. Jeddah)"}
            className="bg-transparent outline-none placeholder:text-[#9a9a9a] w-40 sm:w-48"
            data-testid="partners-city-input"
          />
          {city && (
            <button
              type="button"
              onClick={clearCity}
              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[#525252] hover:text-[#0a0a0a]"
              data-testid="partners-city-clear"
              aria-label={isAr ? "مسح المدينة" : "Clear city"}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        {quickCities.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => applyCity(c)}
            className={`text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              city === c
                ? "bg-[#0a0a0a] text-[#e8c873] border-[#0a0a0a]"
                : "bg-white text-[#525252] border-[#0a0a0a]/10 hover:border-[#c9a55c]"
            }`}
            data-testid={`partners-city-chip-${c}`}
          >
            {c}
          </button>
        ))}
      </form>

      {showEmpty ? (
        <div
          className="rounded-2xl border border-dashed border-[#0a0a0a]/15 bg-[#fafaf7] p-6 text-center text-sm text-[#525252]"
          data-testid="partners-city-empty"
        >
          {isAr
            ? `لا يوجد مزوّدون في «${city}» ضمن هذا القسم حالياً.`
            : `No providers in "${city}" for this section yet.`}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map((b) => (
            <Link
              key={b.id}
              to={`/company/${b.slug || b.id}`}
              className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] hover:shadow-md hover:-translate-y-0.5 transition-all p-4 flex items-start gap-3"
              data-testid={`partner-card-${b.id}`}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                   style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}>
                <ShieldCheck className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold text-[#0a0a0a] truncate">
                  {isAr ? b.name_ar : (b.name_en || b.name_ar)}
                </div>
                {b.city && (
                  <div className="text-[10px] text-[#8a8a8a] mt-0.5 inline-flex items-center gap-1">
                    <MapPin className="w-2.5 h-2.5" /> {b.city}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
